import { Box, InputBase, Typography, useTheme } from "@mui/material";
import type { ChangeEvent, Ref } from "react";
import { useFlatRowValueColor } from "web_myplann/Shared/Hooks/useFlatRowValueColor";

export type FlatRowTextareaProps = {
	label: string;
	value?: string;
	onChange?: (value: string) => void;
	onBlur?: () => void;
	inputRef?: Ref<HTMLTextAreaElement>;
	placeholder?: string;
	rows?: number;
	isLast?: boolean;
	error?: boolean;
	helperText?: string;
};

export const FlatRowTextarea = ({
	label,
	value,
	onChange,
	onBlur,
	inputRef,
	placeholder,
	rows = 3,
	isLast,
	error,
	helperText,
}: FlatRowTextareaProps) => {
	const { palette, spacingTokens } = useTheme();
	const valueColor = useFlatRowValueColor();

	const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		onChange?.(event.target.value);
	};

	return (
		<Box borderBottom={isLast ? "none" : `1px solid ${palette.surface.border}`}>
			<Box
				width="100%"
				px={spacingTokens.lg}
				py={1.5 /* no matching spacing token */}
			>
				<Typography
					variant="body2"
					color={error ? palette.error.main : palette.grey[500]}
					mb={0.5 /* no matching spacing token */}
				>
					{label}
				</Typography>
				<InputBase
					value={value ?? ""}
					onChange={handleChange}
					onBlur={onBlur}
					inputRef={inputRef}
					placeholder={placeholder}
					multiline
					minRows={rows}
					sx={{
						width: "100%",
						p: 0,
						"& textarea": {
							fontSize: 14,
							color: valueColor,
							resize: "none",
							"&::placeholder": {
								color: palette.grey[300],
								fontWeight: 400,
								opacity: 1,
							},
						},
					}}
				/>
			</Box>
			{error && helperText && (
				<Typography
					component="p"
					variant="caption"
					color={palette.error.main}
					px={spacingTokens.lg}
					pb={0.6 /* no matching spacing token */}
				>
					{helperText}
				</Typography>
			)}
		</Box>
	);
};
